import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useGlobalContext } from "context/global";
import { bulkUpdateLines, updateShippingMethod } from "actions/account/subscription";
import Loader from "components/loader";
import { toast } from "react-toastify";
import { TOGGLE_SAVE_LOADING, UPDATE_DELIVERY_PRICE } from "utils/action-types";
import { findClosestBox } from "utils/cart";
import ErrorHoverButton from "../error-hover-button";
import { trackSaveChanges } from "services/analytics";
import { UPDATE_SUBSCRIPTION_BOX } from "utils/action-types";
import clsx from "clsx/lite";

const SaveChangesButton = ({ inBanner = false }) => {
	const { activeBox, totalWeight, deliveryPrice, saveInProgress } = useSelector((state) => state.account.subscription);
	const {
		translation, cartSettings, subscriptionSettings, cartSettings: { shippingCost, freeShippingThreshold, min_products_required }
	} = useGlobalContext();

	const dispatch = useDispatch();
	const minBox = findClosestBox(cartSettings.boxes, totalWeight);
	const { standardShippings = [], premiumShippings = [], upsShippings = [] } = subscriptionSettings?.shippingMethods || {};
	const closestBox = findClosestBox(cartSettings.boxes, totalWeight);

	const standard = standardShippings.find(s => s.boxSize === closestBox.size.value);
	const premium = premiumShippings.find(s => s.boxSize === closestBox.size.value);
	const ups = upsShippings.find(s => s.boxSize === closestBox.size.value);

	const shouldUpdateShippingMethod = standard && premium && ups &&
		![standard.price, premium.price, ups.price].includes(deliveryPrice);

	const saveChanges = async () => {
		dispatch({ type: TOGGLE_SAVE_LOADING });
		const response = await dispatch(bulkUpdateLines(shouldUpdateShippingMethod));
		if (response?.success) {
			if (response.subscription) {
				trackSaveChanges(response.subscription);
			}

			if (shouldUpdateShippingMethod) {
				dispatch(updateShippingMethod(standard.price, standard.title));
				dispatch({ type: UPDATE_DELIVERY_PRICE, payload: standard.price });
			} else {
				const newDeliveryPrice = totalWeight < freeShippingThreshold ? shippingCost : 0;
				dispatch({ type: UPDATE_DELIVERY_PRICE, payload: newDeliveryPrice });
			}

			toast.success(translation?.subscription?.changes_saved);
		} else {
			dispatch({ type: TOGGLE_SAVE_LOADING });
			toast.error(response.message);
		}
	};


	useEffect(() => {
		if (!activeBox || !minBox) return;
		// Update the active box if minBox size is greater than the active box size OR
		// if minBox size is less than the active box size and the active box is not selected from the box selector
		const shouldUpdateBox =
			minBox.size.value > activeBox.size?.value ||
			(minBox.size.value < activeBox.size?.value && !activeBox.userSelected);

		if (shouldUpdateBox) {
			dispatch({ type: UPDATE_SUBSCRIPTION_BOX, payload: minBox });
		}
	}, [totalWeight, activeBox, minBox, dispatch]);

	const isButtonDisabled = minBox.size.value !== activeBox.size?.value ||
		saveInProgress || totalWeight < min_products_required;

	const productsLeft = activeBox.size?.value - totalWeight;
	const errorMessage = productsLeft > 0 ?
		translation.subscription?.cart.add_x_to_save_changes?.replace("{x}", productsLeft) : null;

	return (
		<div className={clsx("flex flex-col text-center", inBanner ? "w-11/12 lg:w-auto lg:p-4" : "border-t p-4")}>
			{!inBanner && <span className="text-sm pb-2 text-green-discount">
				{translation.subscription?.price_calculation_message}
			</span>}
			<ErrorHoverButton
				id="save-changes-button"
				text={saveInProgress ? <Loader ring /> : translation.subscription?.save_changes}
				onClick={saveChanges}
				isDisabled={isButtonDisabled}
				errorMessage={errorMessage}
				className={clsx(inBanner && "px-10 btn-orange")}
			/>
		</div>
	);
};

export default SaveChangesButton;